/**
 * Thin fetch wrapper for the Lunchtool API. Sends the X-Tenant header and an
 * Authorization Bearer token from the Keycloak token store. Uses
 * VITE_APP_HOST_API as the base URL.
 */

import { useAuthDriver } from '@/auth'
import { useTokenStore } from '@/stores/token'

const baseUrl = (https://atabix.api.febo.infrastructure.atabix.nl/ as string) || ''
const tenant = (atabix as string) || ''

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message)
  }
}

// Thrown instead of a plain ApiError when the request was aborted for taking too long, so
// callers (and tests) can tell a timeout apart from a genuine server response with `instanceof`.
export class ApiTimeoutError extends ApiError {
  constructor() {
    super('Request timed out.', 408, null)
  }
}

/**
 * Picks the real, often Dutch, server message out of a Laravel-style error body instead of
 * letting callers fall back to the HTTP status text. For a 422 the top-level `message` is just
 * Laravel's generic "The given data was invalid." wrapper, so the field-specific `errors` map
 * is preferred there; for every other status the top-level `message` is the real reason.
 */
function getServerErrorMessage(body: unknown, status: number): string | undefined {
  const parsedBody = body as { message?: string; errors?: Record<string, string[]> } | null

  if (status === 422 && parsedBody?.errors && Object.keys(parsedBody.errors).length > 0) {
    return Object.values(parsedBody.errors).flat().join(' ')
  }

  return parsedBody?.message || undefined
}

// A 401 already proves the server rejected the current token, so force a refresh attempt
// regardless of what the client thinks its remaining validity is.
const forceRefreshMinValiditySeconds = 999_999

// A backend that accepts the connection but never responds must not hang the app forever.
// 20s is generous for this app's requests while still giving the user feedback within their
// patience.
const requestTimeoutMilliseconds = 20_000

let refreshPromise: Promise<boolean> | null = null

// Concurrent 401s share this single in-flight promise instead of each triggering their own
// refresh -- otherwise a burst of failing requests would stampede the token endpoint.
function refreshAccessToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = performTokenRefresh().finally(() => {
      refreshPromise = null
    })
  }

  return refreshPromise
}

async function performTokenRefresh(): Promise<boolean> {
  const token = useTokenStore()

  try {
    const keycloak = token.getClient()
    await keycloak.updateToken(forceRefreshMinValiditySeconds)
    token.setAccess(keycloak.token || undefined)
    token.setRefresh(keycloak.refreshToken || undefined)

    return Boolean(keycloak.token)
  } catch {
    return false
  }
}

function toFormData(body: Record<string, unknown>): FormData {
  const formData = new FormData()
  for (const [key, value] of Object.entries(body)) {
    if (value === null || value === undefined) continue
    if (value instanceof File) {
      formData.append(key, value)
    } else {
      formData.append(key, String(value))
    }
  }
  return formData
}

async function request<T>(path: string, init: RequestInit = {}, isRetryAfterRefresh = false): Promise<T> {
  const headers = new Headers(init.headers ?? {})
  headers.set('Accept', 'application/json')
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')
  if (tenant) headers.set('X-Tenant', tenant)

  const accessToken = useTokenStore().access

  // Sending the request without a token only ever produces an auth failure, and the API
  // answers a tokenless request with a 500 rather than a 401, so do not send it at all.
  if (!accessToken) {
    throw new ApiError('Unauthenticated.', 401, null)
  }

  headers.set('Authorization', `Bearer ${accessToken}`)

  const abortController = new AbortController()
  const timeoutId = setTimeout(() => abortController.abort(), requestTimeoutMilliseconds)

  let response: Response
  try {
    response = await fetch(`${baseUrl.replace(/\/$/, '')}${path}`, {
      ...init,
      headers,
      signal: abortController.signal,
    })
  } catch (error) {
    // An aborted fetch rejects instead of resolving to a Response, so it can never reach the
    // 401 refresh-and-retry path below -- surface it as a distinguishable timeout instead of
    // letting the raw AbortError leak out as a generic failure.
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiTimeoutError()
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
  }

  // A transient 401 (backend hiccup, a race right after a role change, clock skew) must not
  // nuke the whole session. Refresh the token once and retry this same request once before
  // giving up -- `isRetryAfterRefresh` guarantees this only ever happens once per request.
  if (response.status === 401 && !isRetryAfterRefresh) {
    const refreshed = await refreshAccessToken()
    if (refreshed) {
      return request<T>(path, init, true)
    }
  }

  if (!response.ok) {
    let body: unknown = null
    try {
      body = await response.json()
    } catch {
      // ignore
    }

    // A 401 that survives a token refresh (or one that couldn't be refreshed at all) means
    // the session is genuinely gone, so fall back to a full Keycloak logout.
    if (response.status === 401) {
      await useAuthDriver().logout()
    }

    throw new ApiError(getServerErrorMessage(body, response.status) ?? response.statusText, response.status, body)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  postForm: <T>(path: string, body: Record<string, unknown>) => request<T>(path, { method: 'POST', body: toFormData(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  patchForm: <T>(path: string, body: Record<string, unknown>) => request<T>(path, { method: 'PATCH', body: toFormData(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}
